import type { Extended, Layers, Level, Resolution, Scope } from "./types";

/**
 * THE CASCADE — ported from `api/src/helpers/foSettingForUser.js`, with ONE
 * deliberate divergence, described below.
 *
 * The server remains the source of truth for what a participant sees
 * (`GET /settings/fo/me` returns already-resolved values). We replay the cascade
 * for the ORGANISER PANEL, which needs two things a resolved value cannot carry:
 *   — PROVENANCE (which level won),
 *   — an instant preview when switching language or typology.
 *
 * ═══ ORDER — TYPOLOGY OUTRANKS LANGUAGE ═══
 *   1. typosV2[typo] of the read language
 *   2. typosV2[typo] of the default language
 *   3. flat key of the read language
 *   4. flat key of the default language
 *
 * Counter-intuitive but real consequence: a typology override written in the
 * DEFAULT language beats a value written in the participant's own language.
 *
 * ═══ EVERY LEVEL DECIDES BY PRESENCE ═══
 *
 * A key that is THERE wins, whatever its value. `false`, `0` and `''` are values,
 * not absences. To inherit again you REMOVE the key — that is what "Reprendre
 * l'héritée" does in the panel. Two distinct states, two distinct gestures.
 *
 * ⚠️ THIS IS THE DIVERGENCE, AND IT IS THE POINT. The reference resolves typology
 * levels with `||`, so truthiness decides there while presence decides for flat
 * keys. A typology override set to "no" is therefore skipped, and switching an
 * option off for exhibitors does not switch it off — the toggle springs back.
 * An organiser cannot express "off for this typology" at all.
 *
 * ⚠️ THE SERVER STILL HAS IT, in two places, and both must be fixed before V2
 * serves a single visitor — otherwise this preview tells the organiser something
 * the site will not do:
 *   — `api/src/routes/settingsFo/me/index.js`, the `settingsAll.reduce`: its
 *     guard tests the override for truthiness, then the `||` chain skips it again.
 *   — `api/src/helpers/foSettingForUser.js`, `pickFoSetting`.
 * That fix is NOT a search-and-replace: existing events hold `''` overrides
 * written by V1 to mean "cleared", and presence would turn them into deliberate
 * blanks. They have to be read as absences, or migrated away, first.
 */

const EMPTY: Record<string, unknown> = {};

function typoLayer(
  extended: Extended,
  typoId: number | null,
): Record<string, unknown> {
  if (!typoId) return EMPTY;
  return extended.typosV2?.[String(typoId)] ?? EMPTY;
}

/** Safe `hasOwnProperty`, including on prototype-less objects. */
function has(target: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(target, key);
}

/** The value as the SERVER would compute it. Mirrors `pickFoSetting`. */
export function resolveValue(
  layers: Layers,
  scope: Scope,
  key: string,
): unknown {
  // Spread: presence wins (including `false`).
  const base: Record<string, unknown> = {
    ...layers.defaultLanguage,
    ...layers.language,
  };

  if (scope.typoId) {
    // PRESENCE, not truthiness: `false` is an answer, not a silence.
    const fromLanguage = typoLayer(layers.language, scope.typoId);
    if (has(fromLanguage, key)) return fromLanguage[key];
    const fromDefault = typoLayer(layers.defaultLanguage, scope.typoId);
    if (has(fromDefault, key)) return fromDefault[key];
  }

  return base[key];
}

/**
 * The value AND where it comes from. This is what the panel consumes: without
 * provenance, four sources compete for one value and nothing says which spoke.
 */
export function resolve(layers: Layers, scope: Scope, key: string): Resolution {
  const value = resolveValue(layers, scope, key);
  const level = findLevel(layers, scope, key);

  // "Written at the current scope", therefore clearable. In the reference
  // language with no typology there is no level underneath: clearing would not
  // restore an inherited value, it would delete the value outright.
  const isSet = scope.typoId
    ? has(typoLayer(layers.language, scope.typoId), key)
    : scope.languageId !== scope.defaultLanguageId && has(layers.language, key);

  return { value, level, isSet };
}

function findLevel(layers: Layers, scope: Scope, key: string): Level {
  if (scope.typoId) {
    if (has(typoLayer(layers.language, scope.typoId), key))
      return "typo-language";
    if (has(typoLayer(layers.defaultLanguage, scope.typoId), key))
      return "typo-default";
  }

  // Flat keys are decided by PRESENCE (spread merge).
  // In the reference language, "the language" IS the shared value: it is the very
  // same row, so announcing an override there would be wrong.
  if (scope.languageId !== scope.defaultLanguageId && has(layers.language, key))
    return "language";
  if (has(layers.defaultLanguage, key)) return "default";
  if (has(layers.language, key)) return "default";

  return "unset";
}

/**
 * Where to WRITE, given the current scope. This is the body `PUT /settings/fo`
 * expects — `{ languageId, extended }` — which the server deep-merges, so we send
 * only the changed key and never the whole object.
 */
/**
 * The dotted path of the override written at this scope — the vocabulary
 * `PUT /settings/fo` now takes in its `unset` list.
 *
 * A merge cannot remove a key, so removal has to be NAMED. Built here, next to
 * `writePath`, because the two must agree: what one writes is exactly what the
 * other must be able to take away.
 */
export function clearPath(
  scope: Scope,
  key: string,
): { languageId: number; unset: string[] } {
  return {
    languageId: scope.languageId,
    unset: [scope.typoId ? `typosV2.${scope.typoId}.${key}` : key],
  };
}

export function writePath(
  scope: Scope,
  key: string,
  value: unknown,
): { languageId: number; extended: Extended } {
  if (scope.typoId) {
    return {
      languageId: scope.languageId,
      extended: { typosV2: { [String(scope.typoId)]: { [key]: value } } },
    };
  }
  return { languageId: scope.languageId, extended: { [key]: value } };
}
